import { Button, Flex } from "@eworkit/iris";
import { FieldArray, Formik, FormikProps } from "formik";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { IEditableSectionRenderProps, useEditableSubmit } from "@/components/EditableSection";
import { FormikError, FormikTextInput } from "@/components/Formik";
import { Group } from "@/components/Group";
import { FormikSelect } from "@/components/Select";
import { Text } from "@/components/Text";
import { fetchPaymentTypes, IPaymentType, ISupplierBankingDetails } from "@/data/companies";
import { FormikCheckbox, FormikForm } from "@/formikComponents";
import { FormikGroupedCountryCodesSingleSelect } from "@/views/components/Codelists";
import { getBankAccountLabel } from "@/views/Settings/CompanySettings/BankingDetails/CompanyBankingDetailsPreview";
import {
  bankDetailInitialValue,
  IBankingDetails,
} from "@/views/Settings/CompanySettings/BankingDetails/CompanyBankingSection";
import { ICompanyFormikProps } from "@/views/Settings/CompanySettings/CompanyData/types";

interface IBankingDetailsFormProps extends IEditableSectionRenderProps {
  form: FormikProps<IBankingDetails>;
}

const BankingDetailsForm = ({ form, editableProps }: IBankingDetailsFormProps) => {
  const { t } = useTranslation(["session", "common"]);

  useEditableSubmit(form, editableProps);

  const [paymentOptionsList, setPaymentOptionsList] = useState<Map<string, IPaymentType[]>>(new Map());

  const getOptionForCountry = async (selectedCountry: string) => {
    const options = new Map(paymentOptionsList);

    if (!options.has(selectedCountry)) {
      const newOptions = await fetchPaymentTypes(selectedCountry);

      options.set(selectedCountry, newOptions);

      setPaymentOptionsList(options);
    }
  };

  const getPaymentOptions = (selectedCountry: string) => {
    getOptionForCountry(selectedCountry);
  };

  const existsCountryWithPaymentType = (paymentType: Pick<IPaymentType, "countryCode" | "paymentTypeId">): boolean =>
    form.values.bankingDetails.some(
      item => item.countryCode === paymentType.countryCode && Number(item.paymentTypeId) === paymentType.paymentTypeId,
    );

  const getUnusedOptions = (countryCode: string, options: IPaymentType[]) =>
    options.filter(
      item =>
        !existsCountryWithPaymentType({
          countryCode: countryCode,
          paymentTypeId: item.paymentTypeId,
        }),
    );

  const getOptions = (bankDetail: ISupplierBankingDetails) => {
    const { id, countryCode, paymentTypeId, paymentName } = bankDetail;

    const options = paymentOptionsList.get(countryCode) || [];

    if (id)
      return [
        {
          value: paymentName,
          code: paymentTypeId,
        },
      ];

    const unusedOptions = getUnusedOptions(countryCode, options);

    return unusedOptions.map(item => ({
      value: item.name,
      code: item.paymentTypeId,
    }));
  };

  const getPaymentType = (countryCode: string, payment: number) => {
    if (countryCode && paymentOptionsList?.has(countryCode)) {
      return paymentOptionsList.get(countryCode)?.find(item => item.paymentTypeId === payment);
    }

    return undefined;
  };

  const hasActive = useMemo(() => {
    return form.values.bankingDetails.filter(detail => detail.status === "ACTIVE")?.length;
  }, [form.values.bankingDetails]);

  const handleCountryChange = (newCountry: string | undefined, index: number) => {
    form.setFieldValue(`bankingDetails[${index}].paymentTypeId`, null);
    form.setFieldValue(`bankingDetails[${index}].paymentName`, null);

    if (newCountry) getPaymentOptions(newCountry);
  };

  return (
    <FormikForm>
      <Flex flexDirection="column" gap="sm" mt="xl">
        <FormikError name="bankingDetails" data-test="error-bankingDetails" />

        <FieldArray name="bankingDetails">
          {({ push }) => (
            <>
              {hasActive ? (
                form.values.bankingDetails.map(
                  (bankDetail: ISupplierBankingDetails, index: number) =>
                    bankDetail.status === "ACTIVE" && (
                      <Flex key={index} flexDirection="column" gap="sm">
                        <Group
                          label={t("session:USERS.SETTINGS.BANKING_DETAILS.FORM.COUNTRY")}
                          direction="column"
                          required>
                          <FormikGroupedCountryCodesSingleSelect
                            size="md"
                            disabled={!!bankDetail.id}
                            name={`bankingDetails[${index}].countryCode`}
                            onChange={newCountry => handleCountryChange(newCountry, index)}
                          />

                          <FormikError name={`bankingDetails[${index}].countryCode`} data-test="error-countryCode" />
                        </Group>

                        <Group
                          label={t("session:USERS.SETTINGS.BANKING_DETAILS.FORM.TYPE")}
                          direction="column"
                          required>
                          <FormikSelect
                            required
                            size="md"
                            simpleValue
                            isClearable={false}
                            value={bankDetail.paymentTypeId}
                            options={getOptions(bankDetail) || []}
                            name={`bankingDetails[${index}].paymentTypeId`}
                            isDisabled={!bankDetail.countryCode || !!bankDetail.id}
                            onChange={newPaymentType =>
                              form.setFieldValue(
                                `bankingDetails[${index}].paymentName`,
                                getPaymentType(bankDetail.countryCode, newPaymentType)?.name,
                              )
                            }
                          />

                          <FormikError
                            name={`bankingDetails[${index}].paymentTypeId`}
                            data-test="error-paymentTypeId"
                          />
                        </Group>

                        {(getPaymentType(bankDetail.countryCode, bankDetail.paymentTypeId)?.isBankNumberRequired ||
                          bankDetail.bankNumber) && (
                          <Group
                            direction="column"
                            required
                            label={t("session:USERS.SETTINGS.BANKING_DETAILS.FORM.BANK_NUMBER", {
                              value: getBankAccountLabel(bankDetail),
                            })}>
                            <FormikTextInput
                              type="text"
                              required
                              clearable={false}
                              disabled={!bankDetail.paymentTypeId}
                              name={`bankingDetails[${index}].bankNumber`}
                            />

                            <FormikError name={`bankingDetails[${index}].bankNumber`} data-test="error-bankNumber" />
                          </Group>
                        )}

                        {(getPaymentType(bankDetail.countryCode, bankDetail.paymentTypeId)?.isSwiftRequired ||
                          bankDetail.swift) && (
                          <Group
                            direction="column"
                            label={t("session:USERS.SETTINGS.BANKING_DETAILS.FORM.SWIFT")}
                            required>
                            <FormikTextInput
                              required
                              type="text"
                              clearable={false}
                              disabled={!bankDetail.paymentTypeId}
                              name={`bankingDetails[${index}].swift`}
                            />
                            <FormikError name={`bankingDetails[${index}].swift`} data-test="error-swift" />
                          </Group>
                        )}

                        {(getPaymentType(bankDetail.countryCode, bankDetail.paymentTypeId)?.isIbanRequired ||
                          bankDetail.iban) && (
                          <Group
                            direction="column"
                            label={t("session:USERS.SETTINGS.BANKING_DETAILS.FORM.IBAN")}
                            required>
                            <FormikTextInput
                              required
                              type="text"
                              clearable={false}
                              disabled={!bankDetail.paymentTypeId}
                              name={`bankingDetails[${index}].iban`}
                            />
                            <FormikError name={`bankingDetails[${index}].iban`} data-test="error-iban" />
                          </Group>
                        )}

                        <Flex justifyContent="space-between" gap="xs">
                          <FormikCheckbox
                            size="lg"
                            checked={bankDetail.factoringCompany}
                            name={`bankingDetails[${index}].factoringCompany`}>
                            <Text>{t("session:USERS.SETTINGS.BANKING_DETAILS.FORM.FACTORING_COMPANY")}</Text>
                          </FormikCheckbox>

                          <FormikCheckbox
                            size="lg"
                            checked={bankDetail.primary}
                            name={`bankingDetails[${index}].primary`}>
                            <Text>{t("session:USERS.SETTINGS.BANKING_DETAILS.FORM.PRIMARY")}</Text>
                          </FormikCheckbox>
                        </Flex>

                        <Flex justifyContent="flex-end" gap="sm">
                          <Button onClick={() => form.setFieldValue(`bankingDetails[${index}].status`, "INACTIVE")}>
                            {t("common:BUTTONS.REMOVE")}
                          </Button>
                        </Flex>
                      </Flex>
                    ),
                )
              ) : (
                <Text size="md">{t("USERS.SETTINGS.BANKING_DETAILS.FORM.NO_DETAILS")}</Text>
              )}

              <Flex justifyContent="flex-end" gap="sm">
                <Button onClick={() => push(bankDetailInitialValue)}>{t("common:BUTTONS.ADD_NEW")}</Button>
              </Flex>
            </>
          )}
        </FieldArray>
      </Flex>
    </FormikForm>
  );
};

interface IBankingDetailsFormikProps extends ICompanyFormikProps<IBankingDetails>, IEditableSectionRenderProps {}

export const CompanyBankingSectionEdit = ({
  onSubmit,
  initialValues,
  validationSchema,
  ...rest
}: IBankingDetailsFormikProps) => (
  <Formik enableReinitialize onSubmit={onSubmit} initialValues={initialValues} validationSchema={validationSchema}>
    {(form: FormikProps<IBankingDetails>) => <BankingDetailsForm {...rest} form={form} />}
  </Formik>
);
